import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { isMobileAppMode, mobileNavigateBack } from '../../utils/mobileAppMode';
import './arrowpuzzle.css';
import { fetchThemeData } from '../../admin/themeSlice';
import { selectAdminToken } from '../../admin/sessionSlice';
import { setBackButtonUrl, setCompanyLogoUrl } from '../uiSlice';
import { sanitizeCompanyLogoUrl } from '../../functions/companyLogoUrl';

import {
  getArrowPuzzleForCategory,
  coerceLabelFontScale,
  normalizeLabelRotationDeg,
} from '../../utils/arrowPuzzleGenerator';
import {
  allArrowCategoriesCompleteFromMap,
  isArrowCategoryPlayedFromMap,
} from '../../utils/arrowRunProgress';
import { arrowUpSaveCategory } from '../../functions/arrowUpStageApi';
import { fetchStageProgress } from '../../functions/fetchStageProgress';
import { setArrowByCategory } from '../arrowGameSlice';
import { s3PublicUrl } from '../../utils/s3PublicUrl';

const BASE_LABEL_FONT_FR = 0.42;

/* ─────────────────────────────────────────────────────────────────────────────
   ARC-LENGTH SNAKE ANIMATION HELPERS
   These replace the old rigid rowShift/colShift translation.
   The key idea:
     1. Convert the grid-cell polyline into world-pixel points.
     2. At every bend, insert a quadratic Bézier arc (radius = ~40% of cell)
        so the body navigates corners smoothly instead of snapping.
     3. Extend the path several cells past the board edge so the body fully
        exits before the animation ends.
     4. Sample the resulting curve into a dense {x,y,d} table (arc-length
        parametrization) so we can seek to any position in O(log n).
     5. During animation, advance a sliding window of fixed pixel-length
        (= the body's length in px) from start → exit. Each frame we re-sample
        N points along that window to build the SVG path `d` string.
   ───────────────────────────────────────────────────────────────────────── */

/**
 * Build a smooth arc-length path from expanded grid cells.
 * @param {Array<[number,number]>} cells  – array of [row, col] (already expanded)
 * @param {number} cellWidth
 * @param {number} cellHeight
 * @returns {{ samples: Array<{x,y,d}>, totalLen: number }}
 */
function buildArcPath(cells, cellWidth, cellHeight) {
  const toXY = ([r, c]) => ({ x: c * cellWidth + cellWidth / 2, y: r * cellHeight + cellHeight / 2 });
  const pts = cells.map(toXY);
  const R = Math.min(cellWidth, cellHeight) * 0.42; // corner-arc radius in px

  // Inject quadratic-Bézier control structures at every bend
  const smooth = [];
  for (let i = 0; i < pts.length; i++) {
    if (i === 0 || i === pts.length - 1) {
      smooth.push(pts[i]);
      continue;
    }
    const prev = pts[i - 1], cur = pts[i], next = pts[i + 1];
    const dx1 = cur.x - prev.x, dy1 = cur.y - prev.y;
    const dx2 = next.x - cur.x, dy2 = next.y - cur.y;
    const len1 = Math.hypot(dx1, dy1) || 1;
    const len2 = Math.hypot(dx2, dy2) || 1;
    const dot = (dx1 / len1) * (dx2 / len2) + (dy1 / len1) * (dy2 / len2);
    if (Math.abs(dot) < 0.99) {
      // It's a corner – replace the vertex with a bezier arc
      const rr = Math.min(R, len1 * 0.48, len2 * 0.48);
      smooth.push({
        isBezier: true,
        // entry point (before the corner)
        x: cur.x - (dx1 / len1) * rr, y: cur.y - (dy1 / len1) * rr,
        // quadratic control = the original corner vertex
        cx: cur.x, cy: cur.y,
        // exit point (after the corner)
        ex: cur.x + (dx2 / len2) * rr, ey: cur.y + (dy2 / len2) * rr,
      });
    } else {
      smooth.push(cur);
    }
  }

  // Sample the smooth path into a dense uniform arc-length table
  const BEZIER_STEPS = 16; // sub-samples per bezier arc
  const samples = [];
  let totalLen = 0;
  let prevPt = null;

  const addSample = (x, y) => {
    if (prevPt) totalLen += Math.hypot(x - prevPt.x, y - prevPt.y);
    samples.push({ x, y, d: totalLen });
    prevPt = { x, y };
  };

  for (const p of smooth) {
    if (p.isBezier) {
      for (let s = 0; s <= BEZIER_STEPS; s++) {
        const t = s / BEZIER_STEPS;
        const mt = 1 - t;
        addSample(
          mt * mt * p.x + 2 * mt * t * p.cx + t * t * p.ex,
          mt * mt * p.y + 2 * mt * t * p.cy + t * t * p.ey,
        );
      }
    } else {
      addSample(p.x, p.y);
    }
  }

  return { samples, totalLen };
}

/**
 * Binary-search interpolation: returns {x, y} at arc-distance d along samples.
 */
function sampleArcAt(samples, d) {
  if (!samples || samples.length === 0) return { x: 0, y: 0 };
  if (d <= 0) return samples[0];
  const last = samples[samples.length - 1];
  if (d >= last.d) return last;
  let lo = 0, hi = samples.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (samples[mid].d <= d) lo = mid; else hi = mid;
  }
  const a = samples[lo], b = samples[hi];
  const span = b.d - a.d || 1;
  const t = (d - a.d) / span;
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

/**
 * Build the extended arc path used during animation:
 * original cells + straight exit cells beyond the board edge.
 * The exit extension ensures the body fully leaves the board before t=1.
 */
function buildExtendedArcPath(cells, exitSteps, cellWidth, cellHeight) {
  const n = cells.length;
  const [r1, c1] = cells[n - 1];
  const [r0, c0] = n >= 2 ? cells[n - 2] : cells[n - 1];
  const dr = Math.sign(r1 - r0);
  const dc = Math.sign(c1 - c0);
  const extended = [...cells];
  // Extra +3 cells to guarantee the tail fully exits
  for (let k = 1; k <= exitSteps + 3; k++) {
    extended.push([r1 + dr * k, c1 + dc * k]);
  }
  return buildArcPath(extended, cellWidth, cellHeight);
}

/* ─────────────────────────────────────────────────────────────────────────────
   UNCHANGED UTILITY HELPERS (kept exactly as-is)
   ───────────────────────────────────────────────────────────────────────── */

function themeSoundEnabled(themeData) {
  if (!themeData) return false;
  const v = themeData.sound_toggle ?? themeData.sound_toogle;
  return v === true || String(v).toLowerCase() === 'true';
}

function playUrlSound(url) {
  if (!url || typeof Audio === 'undefined') return;
  try {
    const a = new Audio(url);
    a.volume = 0.92;
    void a.play().catch(() => {});
  } catch {
    /* ignore */
  }
}

function playThemeSound(themeData, key) {
  if (!themeSoundEnabled(themeData)) return;
  const raw = themeData?.[key];
  if (raw == null || String(raw).trim() === '') return;
  const url = s3PublicUrl(raw);
  if (url) playUrlSound(url);
}

function parsePoints(themeData, key, fallback) {
  const n = parseInt(String(themeData?.[key] ?? '').replace(/\s/g, ''), 10);
  if (Number.isFinite(n)) return n;
  return fallback;
}

function parsePointsMagnitude(themeData, key, fallbackMag) {
  const n = parseInt(String(themeData?.[key] ?? '').replace(/\s/g, ''), 10);
  if (Number.isFinite(n) && n !== 0) return Math.abs(n);
  const fb = parseInt(String(fallbackMag ?? '').replace(/\s/g, ''), 10);
  if (Number.isFinite(fb) && fb !== 0) return Math.abs(fb);
  return 1;
}

function isUserDemoBypass(user) {
  if (!user) return false;
  const sid = String(user.sessionId ?? '').toLowerCase();
  const oid = String(user.organizationId ?? '').toLowerCase();
  const reportRole =
    user.user_type ||
    user.userType ||
    user.role ||
    (user.source === 'DEMO' ? 'demobypass' : null) ||
    user.source ||
    'ORG_USER';
  return (
    String(reportRole).toLowerCase() === 'demobypass' ||
    user.source === 'DEMO' ||
    (sid === 'demobypass' && oid === 'demobypass') ||
    sid.startsWith('demo')
  );
}

function AnalogClockIcon({ color }) {
  return (
    <span className="arrow-puzzle-analog-clock" style={color ? { color } : undefined} aria-hidden>
      <span className="arrow-puzzle-analog-clock__face">
        <span className="arrow-puzzle-analog-clock__ticks" />
        <span className="arrow-puzzle-analog-clock__hand arrow-puzzle-analog-clock__hand--hour" />
        <span className="arrow-puzzle-analog-clock__hand arrow-puzzle-analog-clock__hand--min" />
        <span className="arrow-puzzle-analog-clock__cap" />
      </span>
    </span>
  );
}

function isWordlessMode(themeData, puzzle) {
  const twl = themeData?.wordless_mode;
  if (twl === true || String(twl).toLowerCase() === 'true') return true;
  if (twl === false || String(twl).toLowerCase() === 'false') return false;
  const pwl = puzzle?.wordless_mode;
  if (pwl === true || String(pwl).toLowerCase() === 'true') return true;
  if (pwl === false || String(pwl).toLowerCase() === 'false') return false;
  return false;
}

function expandAxisAlignedPolylineToCells(cells) {
  if (!Array.isArray(cells) || cells.length === 0) return [];
  const out = [];
  const seen = new Set();
  const add = (r, c) => {
    const k = `${r},${c}`;
    if (seen.has(k)) return;
    seen.add(k);
    out.push([r, c]);
  };
  for (let i = 0; i < cells.length; i++) {
    const cur = cells[i];
    if (!Array.isArray(cur) || cur.length < 2) continue;
    let r = Number(cur[0]);
    let c = Number(cur[1]);
    if (!Number.isFinite(r) || !Number.isFinite(c)) continue;
    add(r, c);
    if (i >= cells.length - 1) break;
    const nxt = cells[i + 1];
    if (!Array.isArray(nxt) || nxt.length < 2) break;
    const r1 = Number(nxt[0]);
    const c1 = Number(nxt[1]);
    if (!Number.isFinite(r1) || !Number.isFinite(c1)) break;
    while (r !== r1 || c !== c1) {
      if (r !== r1) r += r1 > r ? 1 : -1;
      else c += c1 > c ? 1 : -1;
      add(r, c);
    }
  }
  return out;
}

function arrowSlideDeltaFromCells(cells, arrowFallback) {
  const n = cells?.length ?? 0;
  if (n >= 2) {
    const a = cells[n - 2];
    const b = cells[n - 1];
    const ddr = b[0] - a[0];
    const ddc = b[1] - a[1];
    const absr = Math.abs(ddr);
    const absc = Math.abs(ddc);
    if (absr === 1 && absc === 0) return [Math.sign(ddr) || 1, 0];
    if (absc === 1 && absr === 0) return [0, Math.sign(ddc) || 1];
    if (absr + absc > 1) {
      if (absr >= absc) return [Math.sign(ddr) || 1, 0];
      return [0, Math.sign(ddc) || 1];
    }
  }
  switch (String(arrowFallback || '').toLowerCase()) {
    case 'up': return [-1, 0];
    case 'down': return [1, 0];
    case 'left': return [0, -1];
    case 'right':
    default: return [0, 1];
  }
}

/** Blocked tap: damped wiggle in slide direction, 0 at start and end. */
function reboundShiftFrac(u) {
  const t = Math.min(1, Math.max(0, u));
  // Single forward bump then back (no oscillation), smaller amplitude to avoid overlapping other arrows visually.
  const amp = 0.18;
  return Math.sin(t * Math.PI) * (1 - t) * amp;
}

/* ─────────────────────────────────────────────────────────────────────────────
   COMPONENT
   ───────────────────────────────────────────────────────────────────────── */

const ArrowPuzzle = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const user = useSelector((state) => state.auth.user);
  const reduxStageId = useSelector((state) => state.arrowGame.stageId);
  const { data: themeData } = useSelector((state) => state.theme);
  const companyLogoUrl = useSelector((state) => state.ui.companyLogoUrl);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [arrowPuzzle, setArrowPuzzle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [elapsedSec, setElapsedSec] = useState(0);
  const levelStartedAtRef = useRef(null);
  const [levelScore, setLevelScore] = useState(0);
  const [gamePhase, setGamePhase] = useState('playing');
  const [arrowsState, setArrowsState] = useState([]);
  const [animTick, setAnimTick] = useState(0);
  const [useMobileBg, setUseMobileBg] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );
  const [boardMaxPx, setBoardMaxPx] = useState(560);
  const [rulesModalOpen, setRulesModalOpen] = useState(false);

  const svgRef = useRef(null);
  const arrowsStateRef = useRef(arrowsState);
  const arrowPuzzleRef = useRef(arrowPuzzle);
  // animRef now stores arc-based animation data (replaces rigid row/col shift)
  const animRef = useRef(null);
  const reboundRef = useRef(null);
  const rafRef = useRef(null);
  const gamePhaseRef = useRef(gamePhase);
  const winHandledRef = useRef(false);
  const demoArrowPromoScheduledRef = useRef(false);
  // Cache for built arc paths keyed by `pathIndex_exitSteps`
  const arcCacheRef = useRef(new Map());
  // Temporary click feedback (green/red) keyed by pathIndex.
  const feedbackRef = useRef({});

  const themeDataRef = useRef(themeData);
  useEffect(() => { themeDataRef.current = themeData; }, [themeData]);

  const flashPathFeedback = useCallback((pathIndex, outcome) => {
    const kind = outcome === 'correct' ? 'correct' : 'wrong';
    const until = Date.now() + 650;
    feedbackRef.current = { ...(feedbackRef.current || {}), [pathIndex]: { kind, until } };
    setAnimTick((x) => x + 1);
    window.setTimeout(() => setAnimTick((x) => x + 1), 720);
  }, []);

  useEffect(() => { arrowsStateRef.current = arrowsState; }, [arrowsState]);
  useEffect(() => { arrowPuzzleRef.current = arrowPuzzle; }, [arrowPuzzle]);
  useEffect(() => { gamePhaseRef.current = gamePhase; }, [gamePhase]);

  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    if (!adminToken) return;
    const tid = typeof localStorage !== 'undefined' ? localStorage.getItem('currentTheme') : null;
    dispatch(fetchThemeData({ themeId: tid }));
  }, [dispatch, adminToken]);

  useEffect(() => {
    dispatch(setBackButtonUrl('/rules'));
    return () => { dispatch(setBackButtonUrl(null)); };
  }, [dispatch]);

  useEffect(() => {
    const raw = user?.companyLogoUrl;
    if (!raw || String(raw).trim() === '') return;
    const clean = sanitizeCompanyLogoUrl(raw);
    if (clean && clean !== companyLogoUrl) dispatch(setCompanyLogoUrl(clean));
  }, [user?.companyLogoUrl, companyLogoUrl, dispatch]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    const apply = () => {
      // Desktop (wide screens): lock scroll. Smaller screens (<=1366px): allow scroll.
      // This prevents page scroll on large desktop while still allowing overflow on smaller laptops/tablets.
      const w = typeof window !== 'undefined' ? window.innerWidth : 0;
      document.body.style.overflow = w > 1366 ? 'hidden' : 'auto';
    };
    apply();
    window.addEventListener('resize', apply);
    window.addEventListener('orientationchange', apply);
    return () => {
      window.removeEventListener('resize', apply);
      window.removeEventListener('orientationchange', apply);
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onResize = () => setUseMobileBg(typeof window !== 'undefined' && window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const ro = () => {
      if (typeof window === 'undefined') return;
      const v = Math.min(window.innerWidth - 28, window.innerHeight * 0.62, 780);
      setBoardMaxPx(Math.max(300, Math.floor(v)));
    };
    ro();
    window.addEventListener('resize', ro);
    return () => window.removeEventListener('resize', ro);
  }, []);

  // Clear arc cache when puzzle changes
  useEffect(() => {
    arcCacheRef.current.clear();
  }, [arrowPuzzle]);

  useEffect(() => {
    winHandledRef.current = false;
    demoArrowPromoScheduledRef.current = false;
    if (!themeData) { setIsLoading(false); return; }

    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    animRef.current = null;
    reboundRef.current = null;

    const cat = String(location.state?.category || '').toUpperCase();
    const stageId = location.state?.stageId ?? reduxStageId;

    if (!cat || stageId == null) {
      setArrowPuzzle(null);
      setSelectedCategory(null);
      setArrowsState([]);
      setIsLoading(false);
      navigate('/rules', { replace: true });
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      let remoteMap = {};
      if (user?.userId && adminToken) {
        try {
          const prog = await fetchStageProgress({ userId: user.userId, token: adminToken, stageId });
          remoteMap = prog?.stage?.arrowByCategory && typeof prog.stage.arrowByCategory === 'object'
            ? prog.stage.arrowByCategory : {};
          dispatch(setArrowByCategory(remoteMap));
        } catch { /* proceed */ }
      }
      if (cancelled) return;

      if (isArrowCategoryPlayedFromMap(cat, remoteMap)) {
        setArrowPuzzle(null); setSelectedCategory(null); setArrowsState([]);
        setIsLoading(false);
        navigate('/rules', { replace: true });
        return;
      }

      const puzzle = getArrowPuzzleForCategory(themeData, cat);
      if (!puzzle?.paths?.length) {
        setArrowPuzzle(null); setSelectedCategory(null); setArrowsState([]);
        setIsLoading(false);
        navigate('/rules', { replace: true });
        return;
      }

      setSelectedCategory(cat);
      setArrowPuzzle(puzzle);
      setArrowsState(puzzle.paths.map(() => ({ rowShift: 0, colShift: 0, removed: false })));
      setLevelScore(0);
      setElapsedSec(0);
      levelStartedAtRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();
      setGamePhase('playing');
      setIsLoading(false);
    })();

    return () => { cancelled = true; };
  }, [
    themeData,
    themeData?.arrow_puzzles,
    themeData?.arrow_puzzle,
    navigate,
    location.state,
    user?.userId,
    adminToken,
    reduxStageId,
    dispatch,
  ]);

  useEffect(() => {
    if (gamePhase !== 'playing' || levelStartedAtRef.current == null) return;
    const started = levelStartedAtRef.current;
    const tick = () => {
      const base = typeof performance !== 'undefined' ? performance.now() : Date.now();
      setElapsedSec(Math.max(0, Math.floor((base - started) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [gamePhase, arrowPuzzle]);

  const wordless = !!(arrowPuzzle && themeData && isWordlessMode(themeData, arrowPuzzle));

  useEffect(() => {
    if (gamePhase !== 'playing' || !arrowPuzzle?.paths?.length || winHandledRef.current) return;
    const paths = arrowPuzzle.paths;
    const state = arrowsState;
    const totalCorrect = paths.filter((p) => p?.kind === 'correct').length;

    let won = false;
    if (wordless) {
      if (totalCorrect > 0) {
        won = paths.every((p, i) => (p?.kind === 'correct' ? state[i]?.removed === true : true));
      } else {
        const total = paths.length;
        const gone = state.filter((p) => p?.removed).length;
        won = total > 0 && gone >= total;
      }
    } else if (totalCorrect === 0) {
      const total = paths.length;
      const gone = state.filter((p) => p?.removed).length;
      won = total > 0 && gone >= total;
    } else {
      won = paths.every((p, i) => (p?.kind === 'extra' ? true : state[i]?.removed === true));
    }

    if (!won) return;
    winHandledRef.current = true;
    setGamePhase('complete');
  }, [arrowsState, arrowPuzzle, gamePhase, wordless]);

  const bumpScore = useCallback((delta, opts) => {
    const d = Number(delta);
    if (!Number.isFinite(d)) return;
    const skipSound = opts && opts.skipSound === true;
    const td = themeDataRef.current;
    if (!skipSound && themeSoundEnabled(td)) {
      if (d > 0) playThemeSound(td, 'click_right_answer_sound');
      else if (d < 0) playThemeSound(td, 'click_wrong_answer_sound');
    }
    setLevelScore((prev) => {
      const p = Number(prev);
      const base = Number.isFinite(p) ? p : 0;
      const next = base + d;
      return Math.round(Math.max(-1e6, Math.min(1e9, next)));
    });
  }, []);

  const formatTime = (seconds) => {
    const s = Math.max(0, seconds);
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${String(mins).padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  function buildOccupiedSet(paths, state, excludeIndex, width, height, omitHeadCellOfOthers = false) {
    const occupied = new Set();
    state.forEach((p, i) => {
      if (i === excludeIndex || !p || p.removed) return;
      const raw = Array.isArray(paths[i]?.cells) ? paths[i].cells : [];
      const line = expandAxisAlignedPolylineToCells(raw);
      const toMark = omitHeadCellOfOthers && line.length > 1 ? line.slice(0, -1) : line;
      toMark.forEach((cell) => {
        const r = cell[0] + (p.rowShift || 0);
        const c = cell[1] + (p.colShift || 0);
        if (r >= 0 && r < height && c >= 0 && c < width) occupied.add(`${r},${c}`);
      });
    });
    return occupied;
  }

  function maxSlideStepsHeadRay(myCells, dr, dc, startRowSh, startColSh, width, height, occupied) {
    if (!myCells.length) return 0;
    const head = myCells[myCells.length - 1];
    let k = 0;
    for (;;) {
      const k1 = k + 1;
      const rsh = startRowSh + k1 * dr;
      const csh = startColSh + k1 * dc;
      const hr = head[0] + rsh;
      const hc = head[1] + csh;
      if (hr >= 0 && hr < height && hc >= 0 && hc < width && occupied.has(`${hr},${hc}`)) return k;
      const allOutside = myCells.every((cell) => {
        const r = cell[0] + startRowSh + k1 * dr;
        const c = cell[1] + startColSh + k1 * dc;
        return r < 0 || r >= height || c < 0 || c >= width;
      });
      if (allOutside) return k1;
      k = k1;
      if (k > width + height + 24) return k;
    }
  }

  /* ── Rebound (blocked tap) animation – unchanged ── */
  const runReboundAnimation = (index, row0, col0, dr, dc) => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    reboundRef.current = {
      pathIndex: index,
      row0, col0, dr, dc,
      startTime: performance.now(),
      durationMs: 580,
    };
    const loop = () => {
      const b = reboundRef.current;
      if (!b || b.pathIndex !== index) return;
      const t = (performance.now() - b.startTime) / b.durationMs;
      if (t < 1) {
        setAnimTick((x) => x + 1);
        rafRef.current = requestAnimationFrame(loop);
      } else {
        reboundRef.current = null;
        setAnimTick((x) => x + 1);
        rafRef.current = null;
      }
    };
    setAnimTick((x) => x + 1);
    rafRef.current = requestAnimationFrame(loop);
  };

  /* ── Main move handler – arc-length snake animation ── */
  const moveArrowByDirection = (index) => {
    if (gamePhaseRef.current !== 'playing') return;
    if (isUserDemoBypass(user) && demoArrowPromoScheduledRef.current) return;
    if (animRef.current != null || reboundRef.current != null) return;
    const puzzle = arrowPuzzleRef.current;
    if (!puzzle?.paths?.[index]) return;

    const { width = 10, height = 10, paths = [] } = puzzle;
    const prev = arrowsStateRef.current;
    const current = prev[index];
    if (!current || current.removed) return;

    const occupied = buildOccupiedSet(paths, prev, index, width, height, true);
    const rawCells = Array.isArray(paths[index]?.cells) ? paths[index].cells : [];
    const myCells = expandAxisAlignedPolylineToCells(rawCells);
    if (myCells.length === 0) return;

    if (isUserDemoBypass(user) && !demoArrowPromoScheduledRef.current) {
      demoArrowPromoScheduledRef.current = true;
      window.setTimeout(() => {
        Swal.fire({
          icon: 'info',
          text: 'Thank you for playing. Subscribe to any PLAN to play with your peers.',
          confirmButtonText: 'OK',
          allowOutsideClick: false,
          allowEscapeKey: false,
        }).then(() => {
          if (isMobileAppMode()) {
            mobileNavigateBack();
            return;
          }
          window.location.href = (process.env.REACT_APP_BASE_URL || '') + '/plans';
        });
      }, 400);
    }

    const [dr, dc] = arrowSlideDeltaFromCells(myCells, paths[index]?.arrow);
    const row0 = current.rowShift || 0;
    const col0 = current.colShift || 0;

    // Apply current shift to cells for collision check
    const shiftedCells = myCells.map(([r, c]) => [r + row0, c + col0]);
    const shiftedOccupied = buildOccupiedSet(paths, prev, index, width, height, true);
    const steps = maxSlideStepsHeadRay(myCells, dr, dc, row0, col0, width, height, shiftedOccupied);

    if (steps <= 0) {
      const td = themeDataRef.current;
      const blockMag = parsePointsMagnitude(td, 'blocked_points', parsePointsMagnitude(td, 'wrong_points', 1));
      bumpScore(-blockMag);
      flashPathFeedback(index, 'wrong');
      runReboundAnimation(index, row0, col0, dr, dc);
      return;
    }

    // Immediate feedback while the arrow is moving.
    // Correct lines flash green; wrong lines flash red. If the move later rebounds, we override to red in finish().
    const clickedKind = paths[index]?.kind;
    if (clickedKind === 'correct') flashPathFeedback(index, 'correct');
    else if (clickedKind === 'wrong') flashPathFeedback(index, 'wrong');

    // ── Build / reuse the arc-length path for this arrow + exit steps ──
    const cacheKey = `${index}_${steps}_${row0}_${col0}`;
    if (!arcCacheRef.current.has(cacheKey)) {
      // Shift the cells to their current board position before building the arc
      const cellsForArc = myCells.map(([r, c]) => [r + row0, c + col0]);
      const cellWidth = boardMaxPx / width;
      const cellHeight = boardMaxPx / height;
      arcCacheRef.current.set(
        cacheKey,
        buildExtendedArcPath(cellsForArc, steps, cellWidth, cellHeight),
      );
    }
    const arc = arcCacheRef.current.get(cacheKey);

    // bodyLen = the pixel length of just the arrow body on the arc
    // We measure it as the arc length of the original (un-extended) cells
    const cellWidth = boardMaxPx / width;
    const cellHeight = boardMaxPx / height;
    const bodyArc = buildArcPath(
      myCells.map(([r, c]) => [r + row0, c + col0]),
      cellWidth,
      cellHeight,
    );
    const bodyLen = bodyArc.totalLen;
    // travelDist: how far the head needs to travel so the tail exits
    const travelDist = arc.totalLen - bodyLen;
    const totalCells = myCells.length + steps;
    const durationMs = Math.max(480, Math.min(1800, totalCells * 115));

    animRef.current = {
      pathIndex: index,
      arc,
      bodyLen,
      travelDist,
      startTime: performance.now(),
      durationMs,
      // keep these for finish() score logic
      dr, dc,
      row0, col0,
      row1: row0 + steps * dr,
      col1: col0 + steps * dc,
      steps,
      myCells,
      width,
      height,
    };

    const finish = () => {
      const a = animRef.current;
      if (!a || a.pathIndex !== index) return;

      // Arrow is gone when the tail has cleared the board
      const fullyOutside = a.myCells.every((cell) => {
        const r = cell[0] + a.row1;
        const c = cell[1] + a.col1;
        return r < 0 || r >= a.height || c < 0 || c >= a.width;
      });

      const td = themeDataRef.current;
      const correctPts = Math.max(0, parsePoints(td, 'correct_points', 1));
      const wrongMag = parsePointsMagnitude(td, 'wrong_points', 1);
      const wl = td && puzzle && isWordlessMode(td, puzzle);
      const kind = paths[index]?.kind;

      if (fullyOutside) {
        // Never award points for wrong exits; wordless mode only affects label visibility.
        if (kind === 'correct') {
          bumpScore(correctPts);
          flashPathFeedback(index, 'correct');
        } else if (kind === 'wrong') {
          bumpScore(-wrongMag);
          flashPathFeedback(index, 'wrong');
        } else if (wl) {
          // Fallback for legacy puzzles without kinds in wordless mode.
          bumpScore(correctPts);
          flashPathFeedback(index, 'correct');
        }
      } else {
        bumpScore(-wrongMag);
        flashPathFeedback(index, 'wrong');
      }

      animRef.current = null;
      rafRef.current = null;

      const rowSnap = fullyOutside ? a.row1 : a.row0;
      const colSnap = fullyOutside ? a.col1 : a.col0;

      setArrowsState((p) => {
        const next = [...p];
        next[index] = { ...p[index], rowShift: rowSnap, colShift: colSnap, removed: fullyOutside };
        return next;
      });
      setAnimTick((x) => x + 1);

      if (!fullyOutside) {
        requestAnimationFrame(() => {
          runReboundAnimation(index, a.row0, a.col0, dr, dc);
        });
      }
    };

    const loop = () => {
      const a = animRef.current;
      if (!a || a.pathIndex !== index) return;
      const t = (performance.now() - a.startTime) / a.durationMs;
      if (t < 1) {
        setAnimTick((x) => x + 1);
        rafRef.current = requestAnimationFrame(loop);
      } else {
        finish();
      }
    };
    setAnimTick((x) => x + 1);
    rafRef.current = requestAnimationFrame(loop);
  };

  const onCompleteContinue = () => {
    playThemeSound(themeDataRef.current, 'default_question_sound');
    if (!selectedCategory || !themeData || !user?.userId || !adminToken) {
      navigate('/rules');
      return;
    }
    const stageId = location.state?.stageId ?? reduxStageId;
    if (stageId == null) { navigate('/rules', { replace: true }); return; }

    void (async () => {
      try {
        const save = await arrowUpSaveCategory({
          token: adminToken,
          userId: user.userId,
          themeName: themeData.themename,
          stageId,
          categoryUpper: selectedCategory,
          score: levelScore,
          timeSec: elapsedSec,
        });
        const merged = save?.byCategory && typeof save.byCategory === 'object' ? save.byCategory : {};
        dispatch(setArrowByCategory(merged));
        const doneAll = allArrowCategoriesCompleteFromMap(themeData, merged);
        const cats = Array.isArray(themeData.default_cat)
          ? themeData.default_cat.map((c) => String(c).toUpperCase()) : [];
        const scoresForThankYou = cats.slice(0, 3).map((c) => {
          const row = merged[c];
          return row && Number.isFinite(Number(row.score)) ? Number(row.score) : 0;
        });
        if (doneAll) {
          navigate('/thankyou', {
            replace: true,
            state: {
              fromArrowRun: true, stageId,
              energy: scoresForThankYou[0] ?? 0,
              time: scoresForThankYou[1] ?? 0,
              health: scoresForThankYou[2] ?? 0,
            },
          });
        } else {
          navigate('/rules', { replace: true });
        }
      } catch (e) {
        console.error('[ArrowPuzzle] save category failed', e?.message || e);
        navigate('/rules', { replace: true });
      }
    })();
  };

  const handleBack = useCallback(() => {
    playThemeSound(themeDataRef.current, 'default_question_sound');
    if (gamePhaseRef.current === 'playing' && themeDataRef.current) {
      const td = themeDataRef.current;
      const mag = parsePointsMagnitude(td, 'back_penalty', parsePointsMagnitude(td, 'wrong_points', 1));
      const cur = Number.isFinite(levelScore) ? levelScore : 0;
      bumpScore(-mag, { skipSound: true });
    }
    navigate('/rules');
  }, [levelScore, elapsedSec, navigate, bumpScore]);

  const themeColors = useMemo(() => {
    const t = themeData || {};
    const text = t.text_color || '#6b3a1e';
    const boxBg = t.box_bg_color || '#f2f0ed';
    const badgeBg = t.arrow_puzzle_badge_bg || t.question_bg_color || '#ffd8b1';
    const gridDot = t.arrow_puzzle_grid_dot_color || 'rgba(107, 58, 30, 0.2)';
    const gaugeFill = t.arrow_puzzle_gauge_fill || text;
    const gaugeTrack = t.arrow_puzzle_gauge_track || 'rgba(107, 58, 30, 0.18)';
    const boardBg = t.arrow_puzzle_board_bg || 'rgba(255, 255, 255, 0.88)';
    const toolbarBg = t.arrow_puzzle_toolbar_bg || 'rgba(255, 255, 255, 0.78)';
    const pageSurface = t.arrow_puzzle_page_bg || boxBg;
    const overlayBg = t.arrow_puzzle_overlay_bg || 'rgba(15, 12, 10, 0.55)';
    const overlayCardBg = t.arrow_puzzle_overlay_card_bg || boxBg;
    const overlayCardBorder = t.arrow_puzzle_overlay_card_border || 'rgba(107, 58, 30, 0.2)';
    return { text, boxBg, badgeBg, gridDot, gaugeFill, gaugeTrack, boardBg, toolbarBg, pageSurface, overlayBg, overlayCardBg, overlayCardBorder };
  }, [themeData]);

  const bgUrl = useMemo(() => {
    if (!themeData) return '';
    const name = useMobileBg ? themeData.background_mob : themeData.background_desk;
    return s3PublicUrl(name);
  }, [themeData, useMobileBg]);

  const pointsIconUrl = useMemo(() => s3PublicUrl(themeData?.points_icon), [themeData?.points_icon]);
  const displayLogoUrl = useMemo(
    () => sanitizeCompanyLogoUrl(companyLogoUrl || user?.companyLogoUrl),
    [companyLogoUrl, user?.companyLogoUrl],
  );

  const catHeading =
    selectedCategory && themeData?.cat_items?.[selectedCategory]?.heading
      ? String(themeData.cat_items[selectedCategory].heading) : '';

  const winCategoryLabel = useMemo(
    () => (selectedCategory ? String(selectedCategory).toUpperCase() : ''),
    [selectedCategory],
  );

  const winCategoryInfo = useMemo(() => {
    if (!selectedCategory || !themeData?.cat_items?.[selectedCategory]?.info) return '';
    return String(themeData.cat_items[selectedCategory].info);
  }, [selectedCategory, themeData]);

  const rulesList = useMemo(() => {
    const r = themeData?.rules;
    if (Array.isArray(r)) return r.map((x) => String(x).trim()).filter(Boolean);
    if (r != null && String(r).trim()) return [String(r).trim()];
    return [];
  }, [themeData]);

  if (isLoading) {
    return (
      <div className="arrow-puzzle-page arrow-puzzle-page--themed" style={{ color: themeColors.text }}>
        <div className="arrow-puzzle-loading">Loading…</div>
      </div>
    );
  }

  if (!arrowPuzzle) {
    return (
      <div className="arrow-puzzle-page arrow-puzzle-page--themed" style={{ color: themeColors.text }}>
        <div className="arrow-puzzle-loading">Redirecting…</div>
      </div>
    );
  }

  const { width = 10, height = 10, paths = [] } = arrowPuzzle;
  const cellWidth = boardMaxPx / width;
  const cellHeight = boardMaxPx / height;
  const svgWidth = width * cellWidth;
  const svgHeight = height * cellHeight;
  const svgLabelPad = Math.max(48, Math.min(cellWidth, cellHeight) * 2);
  void animTick;

  const isBusy = animRef.current != null || reboundRef.current != null;
  const isPlaying = gamePhase === 'playing';
  const arrowColor = themeData?.text_color || themeColors.text;
  const puzzleWordColor = themeData?.text_color || themeColors.text;
  const hudTextColor = themeData?.text_color || themeColors.text;
  const hudBarBg = themeData?.arrow_puzzle_hud_bg || themeColors.toolbarBg;
  const categoryChipBg = themeData?.box_bg_color || themeColors.box_bg_color;
  const categoryChipText = themeData?.box_text_color || themeColors.box_text_color;
  const modalBtnBg = themeData?.box_bg_color || themeColors.box_bg_color;
  const modalBtnText = themeData?.box_text_color || '#ffffff';
  const popupBg = themeData?.popup_color || themeData?.box_bg_color || themeColors.overlayCardBg;

  const pageStyle = {
    color: hudTextColor,
    backgroundColor: themeColors.pageSurface,
    ...(bgUrl ? {
      backgroundImage: `url(${bgUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    } : {}),
  };

  const baseUrl = process.env.REACT_APP_BASE_URL || '/';

  /* ── Per-path render: compute SVG points using arc-length snake ── */
  const getPathRenderData = (path, idx) => {
    const cells = Array.isArray(path.cells) ? path.cells : [];
    const cellLine = expandAxisAlignedPolylineToCells(cells);
    if (cellLine.length < 1) return null;

    const arrowState = arrowsState[idx];
    const isRemoved = arrowState?.removed === true;
    const animA = animRef.current;
    const reboundB = reboundRef.current;
    const slidingThis = animA != null && animA.pathIndex === idx;
    const reboundThis = reboundB != null && reboundB.pathIndex === idx;
    if (isRemoved && !slidingThis) return null;

    const baseRow = arrowState?.rowShift || 0;
    const baseCol = arrowState?.colShift || 0;
    const [adr, adc] = arrowSlideDeltaFromCells(cellLine, path.arrow);

    /* ── REBOUND (blocked tap): wiggle the static body in-place ── */
    if (reboundThis) {
      const rawUb = Math.min(1, (performance.now() - reboundB.startTime) / reboundB.durationMs);
      const bump = reboundShiftFrac(rawUb);
      const shiftR = reboundB.row0 + reboundB.dr * bump;
      const shiftC = reboundB.col0 + reboundB.dc * bump;
      const pts = cellLine.map(([r, c]) => ({
        x: (c + shiftC) * cellWidth + cellWidth / 2,
        y: (r + shiftR) * cellHeight + cellHeight / 2,
      }));
      return { pts, isRemoved: false, slidingThis: false, reboundThis: true, alpha: 1, exitScale: 1 };
    }

    /* ── SLIDING: arc-length snake animation ── */
    if (slidingThis && animA) {
      const rawT = Math.min(1, (performance.now() - animA.startTime) / animA.durationMs);
      // easeInOut cubic
      const t = rawT < 0.5 ? 4 * rawT * rawT * rawT : (rawT - 1) * (2 * rawT - 2) * (2 * rawT - 2) + 1;

      const headD = animA.bodyLen + animA.travelDist * t;
      const tailD = headD - animA.bodyLen;

      // Sample N+1 points along [tailD … headD] on the arc
      const N = Math.max(12, Math.ceil(animA.bodyLen / (Math.min(cellWidth, cellHeight) * 0.3)));
      const pts = [];
      for (let k = 0; k <= N; k++) {
        const d = tailD + (headD - tailD) * (k / N);
        pts.push(sampleArcAt(animA.arc.samples, Math.max(0, d)));
      }

      // Fade out as the body finishes exiting
      const alpha = rawT > 0.72 ? Math.max(0, 1 - (rawT - 0.72) / 0.28) : 1;
      return { pts, isRemoved: false, slidingThis: true, reboundThis: false, alpha, exitScale: 1 };
    }

    /* ── AT REST: static polyline at current rowShift/colShift ── */
    const pts = cellLine.map(([r, c]) => ({
      x: (c + baseCol) * cellWidth + cellWidth / 2,
      y: (r + baseRow) * cellHeight + cellHeight / 2,
    }));
    return { pts, isRemoved, slidingThis: false, reboundThis: false, alpha: 1, exitScale: 1 };
  };

  return (
    <div className="arrow-puzzle-page arrow-puzzle-page--themed arrow-puzzle-page--fullscreen" style={pageStyle}>

      <div
        className="arrow-puzzle-fs-hud"
        style={{ color: hudTextColor, backgroundColor: hudBarBg, borderColor: themeColors.overlayCardBorder }}
      >
        <div className="arrow-puzzle-fs-hud-left">
          <div className="arrow-puzzle-fs-hud-stat arrow-puzzle-fs-hud-stat--time">
            <div className="arrow-puzzle-fs-time-cluster" style={{ color: hudTextColor }}>
              <AnalogClockIcon color={hudTextColor} />
              <span className="arrow-puzzle-fs-time" style={{ color: hudTextColor }}>
                {formatTime(elapsedSec)}
              </span>
            </div>
          </div>
        </div>
        <div className="arrow-puzzle-fs-hud-center">
          {selectedCategory ? (
            <span
              className="arrow-puzzle-fs-category-chip"
              style={{ backgroundColor: categoryChipBg, color: categoryChipText }}
            >
              {String(selectedCategory).toUpperCase()}
            </span>
          ) : (
            <span className="arrow-puzzle-fs-category-chip arrow-puzzle-fs-category-chip--placeholder" aria-hidden>
              {'\u00a0'}
            </span>
          )}
        </div>
        <div className="arrow-puzzle-fs-hud-right">
          <button
            type="button"
            className="arrow-puzzle-fs-rules-btn"
            onClick={() => {
              playThemeSound(themeDataRef.current, 'default_question_sound');
              setRulesModalOpen(true);
            }}
            aria-label="Open rules"
            style={{ color: hudTextColor, borderColor: hudTextColor, WebkitTextFillColor: hudTextColor }}
          >
            <span className="arrow-puzzle-fs-rules-q" style={{ color: hudTextColor }} aria-hidden>?</span>
          </button>
          <div className="arrow-puzzle-fs-hud-stat arrow-puzzle-fs-hud-stat--points">
            <div className="arrow-puzzle-fs-points-cluster">
              {pointsIconUrl ? (
                <img src={pointsIconUrl} alt="" className="arrow-puzzle-points-icon-img" width={26} height={26} />
              ) : (
                <span className="arrow-puzzle-points-fallback" aria-hidden>★</span>
              )}
              <span className="arrow-puzzle-fs-score" style={{ color: hudTextColor }}>
                {Number.isFinite(levelScore) ? levelScore : 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="arrow-puzzle-top-heading">
        {catHeading ? (
          <h1 className="arrow-puzzle-main-heading" style={{ color: themeData?.text_color || hudTextColor }}>{catHeading}</h1>
        ) : null}
      </div>

      <div className={`arrow-puzzle-canvas-container${isBusy ? ' arrow-puzzle-canvas-container--sliding' : ''}`}>
        <div className="arrow-puzzle-board-fixed">
          <svg
            ref={svgRef}
            className="arrow-puzzle-canvas"
            width="100%"
            height="auto"
            style={{ maxWidth: boardMaxPx, display: 'block', margin: '0 auto', overflow: 'visible' }}
            viewBox={`${-svgLabelPad} ${-svgLabelPad} ${svgWidth + 2 * svgLabelPad} ${svgHeight + 2 * svgLabelPad}`}
          >
            {/* Grid dots */}
            {Array.from({ length: height * width }).map((_, k) => {
              const r = Math.floor(k / width);
              const c = k % width;
              return (
                <circle
                  key={`dot-${r}-${c}`}
                  cx={c * cellWidth + cellWidth / 2}
                  cy={r * cellHeight + cellHeight / 2}
                  r={Math.max(1.1, Math.min(cellWidth, cellHeight) * 0.06)}
                  fill={themeColors.gridDot}
                />
              );
            })}

            {/* Arrow paths */}
            {paths.map((path, idx) => {
              const renderData = getPathRenderData(path, idx);
              if (!renderData) return null;

              const { pts, isRemoved, slidingThis, reboundThis, alpha } = renderData;
              if (pts.length < 1) return null;

              // Build SVG path `d` string from sampled points
              const dStr = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

              // Arrowhead: direction from last two sampled points (works for any angle, including mid-curve)
              const headPt = pts[pts.length - 1];
              const prevPt = pts.length >= 2 ? pts[pts.length - 2] : pts[0];
              const headAngle = Math.atan2(headPt.y - prevPt.y, headPt.x - prevPt.x) * 180 / Math.PI;
              const headSize = Math.min(cellWidth, cellHeight) * 0.32;

              // Label: anchored to the midpoint of the sampled path
              const cellLine = expandAxisAlignedPolylineToCells(Array.isArray(path.cells) ? path.cells : []);
              const arrowState = arrowsState[idx];
              const baseRow = arrowState?.rowShift || 0;
              const baseCol = arrowState?.colShift || 0;
              const midI = Math.floor(cellLine.length / 2);
              const mid = cellLine[midI] || [0, 0];
              const odx = path.labelDxCells != null ? Number(path.labelDxCells) : 0;
              const ody = path.labelDyCells != null ? Number(path.labelDyCells) : 0;

              // During slide, use the midpoint of sampled pts for label position
              let labelX, labelY;
              if (slidingThis && pts.length > 1) {
                const midPt = pts[Math.floor(pts.length / 2)];
                labelX = midPt.x + odx * cellWidth;
                labelY = midPt.y + ody * cellHeight;
              } else {
                labelX = (mid[1] + baseCol) * cellWidth + cellWidth / 2 + odx * cellWidth;
                labelY = (mid[0] + baseRow) * cellHeight + cellHeight / 2 + ody * cellHeight;
              }

              const fontScale = coerceLabelFontScale(path.labelFontScale != null ? path.labelFontScale : 1);
              const fontSize = Math.max(0.35, cellWidth * BASE_LABEL_FONT_FR * fontScale);
              const rot = normalizeLabelRotationDeg(path.labelRotationDeg != null ? path.labelRotationDeg : 0);
              const labelText = String(path.label || '').trim();
              // Wordless mode controls label visibility in the game.
              const showLabel = wordless && !!labelText && path.kind !== 'extra' && alpha > 0.15;
              const fb = feedbackRef.current ? feedbackRef.current[idx] : null;
              const fbActive = fb && typeof fb === 'object' && Number(fb.until) > Date.now();
              const fbColor =
                fbActive && fb.kind === 'correct'
                  ? '#2e7d32'
                  : fbActive && fb.kind === 'wrong'
                    ? '#c62828'
                    : null;
              const strokeColor = fbColor || arrowColor;

              return (
                <g
                  key={`path${idx}`}
                  style={{ cursor: isRemoved || isBusy || !isPlaying ? 'default' : 'pointer' }}
                  onClick={() => {
                    if (!isRemoved && !isBusy && isPlaying) moveArrowByDirection(idx);
                  }}
                >
                  <g style={{ opacity: alpha }}>
                    {/* Body polyline – smooth bezier corners via the sampled pts */}
                    <path
                      d={dStr}
                      fill="none"
                      stroke={strokeColor}
                      strokeWidth={slidingThis ? 3.4 : 2.75}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity={slidingThis ? 1 : isBusy && !reboundThis ? 0.5 : 1}
                    />

                    {/* Arrowhead – rotated to follow the actual curve tangent */}
                    {!isRemoved && (
                      <g transform={`translate(${headPt.x},${headPt.y}) rotate(${headAngle})`}>
                        <polygon
                          points={`${headSize},0 ${-headSize * 0.5},${-headSize * 0.6} ${-headSize * 0.5},${headSize * 0.6}`}
                          fill={strokeColor}
                          opacity={slidingThis ? 1 : isBusy ? 0.65 : 1}
                        />
                      </g>
                    )}

                    {/* Label */}
                    {showLabel && (
                      <text
                        x={labelX}
                        y={labelY}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        transform={`rotate(${rot}, ${labelX}, ${labelY})`}
                        style={{
                          fontSize,
                          fill: puzzleWordColor,
                          fontWeight: 600,
                          fontFamily: "'Segoe UI', system-ui, sans-serif",
                          pointerEvents: 'none',
                          userSelect: 'none',
                        }}
                      >
                        {labelText}
                      </text>
                    )}
                  </g>
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* Rules modal */}
      {rulesModalOpen ? (
        <div className="arrow-puzzle-rules-overlay" style={{ backgroundColor: themeColors.overlayBg }}>
          <div
            className="arrow-puzzle-rules-card arrow-puzzle-rules-card--simple"
            role="dialog"
            aria-modal="true"
            aria-labelledby="arrow-puzzle-rules-title"
            style={{
              backgroundColor: popupBg,
              color: themeData?.text_color || hudTextColor,
              borderColor: themeColors.popup_color,
            }}
          >
            <h2
              id="arrow-puzzle-rules-title"
              className="arrow-puzzle-rules-title arrow-puzzle-rules-title--simple"
              style={{ color: themeData?.text_color || hudTextColor }}
            >
              How to play
            </h2>

            <div className="arrow-puzzle-rules-content">
              {rulesList.length > 0 ? (
                <ul className="arrow-puzzle-rules-list arrow-puzzle-rules-list--simple">
                  {rulesList.map((line) => (
                    <li key={line} style={{ color: themeData?.text_color || hudTextColor }}>
                      {line}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="arrow-puzzle-rules-empty" style={{ color: themeData?.text_color || hudTextColor }}>
                  No rules are configured for this theme.
                </p>
              )}
            </div>

            <div className="arrow-puzzle-rules-actions">
              <button
                type="button"
                className="arrow-puzzle-rules-close arrow-puzzle-rules-close--simple"
                onClick={() => {
                  playThemeSound(themeDataRef.current, 'default_question_sound');
                  setRulesModalOpen(false);
                }}
                style={{
                  background: themeData?.box_bg_color || modalBtnBg,
                  color: themeData?.box_text_color || modalBtnText,
                  border: 'none',
                }}
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Win overlay */}
    {gamePhase === 'complete' ? (
  <div className="arrow-puzzle-win-overlay" style={{ backgroundColor: themeColors.overlayBg }}>
    <div
      className="arrow-puzzle-win-card"
      style={{
        backgroundColor: popupBg,
        color: themeData?.text_color || themeColors.text,
        borderColor: themeData?.text_color || themeColors.overlayCardBorder,
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={
        winCategoryLabel ? 'arrow-puzzle-win-category' : catHeading ? 'arrow-puzzle-win-heading' : undefined
      }
      aria-describedby={winCategoryInfo ? 'arrow-puzzle-win-info' : undefined}
      aria-label={!winCategoryLabel && !catHeading ? 'Puzzle summary' : undefined}
    >
      <div className="arrow-puzzle-win-icon-wrap" aria-hidden>
        <svg 
          className="arrow-puzzle-win-info-icon" 
          width="48" 
          height="48" 
          viewBox="0 0 48 48" 
          fill="none"
          style={{ color: themeData?.text_color || themeColors.text }}
        >
          <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="2.25" />
          <circle cx="24" cy="15" r="2.35" fill="currentColor" />
          <path d="M24 21.5v14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </div>
      
      {winCategoryLabel ? (
        <p 
          id="arrow-puzzle-win-category" 
          className="arrow-puzzle-win-category"
          style={{ color: themeData?.text_color || themeColors.text }}
        >
          {winCategoryLabel}
        </p>
      ) : null}
      
      {catHeading ? (
        <h2 
          id="arrow-puzzle-win-heading" 
          className="arrow-puzzle-win-heading"
          style={{ color: themeData?.text_color || themeColors.text }}
        >
          {catHeading}
        </h2>
      ) : null}
      
      {winCategoryInfo ? (
        <p 
          id="arrow-puzzle-win-info" 
          className="arrow-puzzle-win-info"
          style={{ color: themeData?.text_color || themeColors.text }}
        >
          {winCategoryInfo}
        </p>
      ) : (
        <p 
          className="arrow-puzzle-win-info arrow-puzzle-win-info--fallback"
          style={{ color: themeData?.text_color || themeColors.text }}
        >
          Score <strong style={{ color: themeData?.text_color || themeColors.text }}>{Number.isFinite(levelScore) ? levelScore : 0}</strong>
          {' · '}
          Time <strong style={{ color: themeData?.text_color || themeColors.text }}>{formatTime(elapsedSec)}</strong>
        </p>
      )}
      
      {winCategoryInfo ? (
        <p 
          className="arrow-puzzle-win-meta"
          style={{ color: themeData?.text_color || themeColors.text }}
        >
          Score <strong style={{ color: themeData?.text_color || themeColors.text }}>{Number.isFinite(levelScore) ? levelScore : 0}</strong>
          {' · '}
          Time <strong style={{ color: themeData?.text_color || themeColors.text }}>{formatTime(elapsedSec)}</strong>
        </p>
      ) : null}
      
      <button
        type="button"
        className="arrow-puzzle-win-btn"
        onClick={onCompleteContinue}
        style={{
          background: popupBg,
          color: themeData?.box_text_color || themeData?.box_text_color || '#fff',
          borderColor: themeData?.box_text_color || themeData?.box_text_color,
        }}
      >
        Continue
      </button>
    </div>
  </div>
) : null}
    </div>
  );
};

export default ArrowPuzzle;
